import * as am5 from '@amcharts/amcharts5';
import am5lang_pt_BR from '@amcharts/amcharts5/locales/pt_BR';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import am5themes_animated from '@amcharts/amcharts5/themes/Animated';
import * as am5xy from '@amcharts/amcharts5/xy';
import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';

const CELL_START_LOCATION = 0.1;
const CELL_END_LOCATION = 0.9;

const CHART_ID_PREFIX = 'dimensoes-idss-chart';

const ESPERA_MAXIMA_QUADRO = 1500;

// Teto para a montagem do gráfico: sem ele, um `am5.ready` que falhe travaria o export. 
const ESPERA_MAXIMA_MONTAGEM = 15000;

type OpcoesDeExport = NonNullable<Parameters<am5plugins_exporting.Exporting['export']>[1]>;
const TITLE_PREFIX = 'Pontuações das Dimensões do IDSS';

const DIMENSIONS: Record<string, { name: string; color: am5.Color; label?: string }> = {
  '0-IDSS': {
    name: '0 - IDSS - Índice de Desempenho da Saúde Suplementar',
    color: am5.color('#F8CBAD'),
    label: 'IDSS',
  },
  '1-IDQS': {
    name: '1 - IDQS - QUALIDADE EM ATENÇÃO À SAÚDE',
    color: am5.color('#F8CBAD'),
  },
  '2-IDGA': {
    name: '2 - IDGA - GARANTIA DE ACESSO',
    color: am5.color('#F8CBAD'),
  },
  '3-IDSM': {
    name: '3 - IDSM - SUSTENTABILIDADE NO MERCADO',
    color: am5.color('#F8CBAD'),
  },
  '4-IDGR': {
    name: '4 - IDGR - GESTÃO DE PROCESSOS E REGULAÇÃO',
    color: am5.color('#BDD7EE'),
  },
};

const SERIES_TYPES: Record<string, { name: string; field: string; color?: string }> = {
  pontuacao: {
    name: 'Pontuação',
    field: 'vl_pontuacao',
  },
  pontuacao_bruta: {
    name: 'Pontuação Bruta',
    field: 'vl_pontuacao_bruta',
  },
  bonificacao: {
    name: 'Bonificação',
    field: 'vl_bonificacao',
  },
  prerequisito: {
    name: 'Pré-requisito RN-507',
    field: 'vl_prerequisito_acreditacao',
    color: '#3F48CC',
  },
  acreditacao: {
    name: 'Acreditação Nivel I',
    field: 'vl_acreditacao_nivel1',
    color: '#A349A4',
  },
};

interface SeriesGroup {
  group: string;
  list: am5xy.XYSeries[];
  hidden: boolean;
}

@Component({
    selector: 'bl-dimensoes-idss-chart',
    template: `<div [id]="chartId"></div>`,
    styleUrls: ['./dimensoes-idss-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DimensoesIdssChartComponent implements AfterViewInit, OnDestroy {
  private titleText: string;
  private filenameValue: string;

  private _root: am5.Root;
  private _chart: am5xy.XYChart;
  private _title: am5.Label;
  private _export: am5plugins_exporting.Exporting;

  private _pronto: Promise<void>;
  private _sinalizarPronto: () => void;

  private _figuras: am5.Picture[] = [];

  private _axis: am5xy.Axis<am5xy.AxisRenderer>[] = [];
  private _series: am5xy.XYSeries[] = [];

  private _yearAxis: am5xy.CategoryAxis<am5xy.AxisRenderer>;
  private _seriesMap: Record<string, SeriesGroup> = {};

  chartId: string;

  constructor() {
    this.chartId = CHART_ID_PREFIX + '_' + Math.floor(Math.random() * 1000);

    this._pronto = new Promise<void>(resolve => (this._sinalizarPronto = resolve));

    this._seriesMap = Object.keys(SERIES_TYPES).reduce((acc, type) => {
      acc[type] = { group: type, list: [], hidden: false };
      return acc;
    }, {});
  }

  ngAfterViewInit(): void {
    am5.ready(() => {
      // Root Element
      const root = am5.Root.new(this.chartId);
      root.setThemes([am5themes_animated.new(root)]);
      root.numberFormatter.set('numberFormat', '#.####');
      root.locale = am5lang_pt_BR;

      // Export
      const exporting = am5plugins_exporting.Exporting.new(root, {
        menu: am5plugins_exporting.ExportingMenu.new(root, {}),
        filePrefix: this.filenameValue ?? 'dimensoes-idss',
        backgroundColor: am5.color(0xffffff),
      });
      const menuButton = exporting.get('menu').getPrivate('menuElement').children[0] as HTMLAnchorElement;
      menuButton.innerHTML = '<i class="pi pi-download p-1"></i>';

      // Chart
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          layout: root.verticalLayout,
          maxTooltipDistance: -1,
        }),
      );

      // Cursor
      const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
      cursor.lineX.set('visible', false);
      cursor.lineY.set('visible', false);

      // Title
      const title = chart.children.unshift(
        am5.Label.new(root, {
          text: TITLE_PREFIX + (this.titleText ? ' - (' + this.titleText + ')' : ''),
          fontSize: 20,
          textAlign: 'left',
          y: 0,
          x: 0,
        }),
      );

      // Logo
      const logo = chart.plotContainer.children.push(
        am5.Picture.new(root, {
          src: 'assets/images/logo-texto-preto.svg',
          width: 100,
          height: 20,
          opacity: 0.7,
          y: am5.p100,
          centerY: am5.p100,
          dx: 80,
          dy: 90,
        }),
      );

      // Create IDSS Legend
      const legendaFaixas = chart.plotContainer.children.push(
        am5.Picture.new(root, {
          src: 'assets/images/legenda-grafico.gif',
          width: 200,
          height: 40,
          x: am5.p100,
          y: am5.p100,
          centerX: am5.p100,
          centerY: am5.p100,
          dy: 90,
        }),
      );

      // Axis
      const categoryAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          height: 50, // space for range labels
          categoryField: 'nr_ano_base',
          renderer: am5xy.AxisRendererX.new(root, {
            opposite: true,
            cellStartLocation: CELL_START_LOCATION,
            cellEndLocation: CELL_END_LOCATION,
          }),
        }),
      );
      categoryAxis.get('renderer').grid.template.set('visible', false);
      categoryAxis.get('renderer').labels.template.set('visible', false);
      this._axis.push(categoryAxis);

      const categoryBrutaAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          height: 50, // space for legends
          categoryField: 'nr_ano_base',
          renderer: am5xy.AxisRendererX.new(root, {
            cellStartLocation: CELL_START_LOCATION,
            cellEndLocation: CELL_END_LOCATION,
          }),
        }),
      );
      categoryBrutaAxis.get('renderer').grid.template.set('visible', false);
      categoryBrutaAxis.get('renderer').labels.template.set('visible', false);
      this._axis.push(categoryBrutaAxis);

      const valueAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          max: 1,
          strictMinMax: true,
          tooltip: am5.Tooltip.new(root, {}),
          renderer: am5xy.AxisRendererY.new(root, {
            minGridDistance: 30,
          }),
        }),
      );
      this._axis.push(valueAxis);

      // Esconde a grade/rótulos automáticos: serão substituídos por marcações
      // fixas de 0,2 em 0,2 (faixas avaliativas do IDSS), independentes da
      // altura da tela.
      valueAxis.get('renderer').grid.template.set('visible', false);
      valueAxis.get('renderer').labels.template.set('visible', false);

      const createValueGridStep = (value: number) => {
        const rangeDataItem = valueAxis.makeDataItem({ value });
        const range = valueAxis.createAxisRange(rangeDataItem);

        range.get('grid').setAll({
          visible: true,
          strokeOpacity: 0.15,
        });

        range.get('label').setAll({
          visible: true,
          text: '{value.formatNumber("0.0")}',
          populateText: true,
        });

        return range;
      };

      [0, 0.2, 0.4, 0.6, 0.8, 1].forEach(createValueGridStep);

      // Create series
      let sampleSeries: am5xy.XYSeries[] = [];
      const seriesMap = this._seriesMap;
      Object.keys(DIMENSIONS).forEach((dim, idx) => {
        const series0 = createSeriesPontuacao(dim);
        this._series.push(series0);
        if (!sampleSeries[0]) {
          sampleSeries[0] = series0;
        }

        const series1 = createSeriesBruta(dim);
        this._series.push(series1);
        if (!sampleSeries[1]) {
          sampleSeries[1] = series1;
        }

        // this._series.push(createSeriesBonificacao(dim));

        const series2 = createSeriesPrerequisito(dim, idx);
        this._series.push(series2);
        if (!sampleSeries[2]) {
          sampleSeries[2] = series2;
        }

        const series3 = createSeriesAcreditacao(dim, idx);
        this._series.push(series3);
        if (!sampleSeries[3]) {
          sampleSeries[3] = series3;
        }
      });

      // Create Legend
      const legend = chart.children.push(
        am5.Legend.new(root, {
          // useDefaultMarker: true,
          x: am5.p50,
          y: am5.p100,
          centerX: am5.p50,
          centerY: am5.p50,
          clickTarget: 'none',
        }),
      );

      legend.markerRectangles.template.setAll({
        width: 20,
        height: 20,
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5.color('#000'),
        fillPattern: createLineBackground('#000'),
        // fill: am5.color('#000'),
      });
      legend.markerRectangles.template.adapters.add('fill', (fill, target) => getLegendColor(fill, target));
      // legend.markerRectangles.template.adapters.add('fillPattern', (fill, target) => getLegendPattern(fill, target));

      // Click for groups
      legend.itemContainers.template.events.on('click', event => {
        const targetSeries = event.target.dataItem.dataContext as am5xy.XYSeries;
        const targetGroup = Object.values(seriesMap).find(groupData =>
          groupData.list.find(series => targetSeries == series),
        );
        targetGroup.hidden = !targetGroup.hidden;
        targetGroup.list.forEach(series => {
          if (targetGroup.hidden) {
            series.hide();
          } else {
            series.show();
          }
        });

        // Force bullets to show
        /*
        if (targetGroup.group == 'pontuacao' && targetGroup.hidden) {
          targetGroup.list.forEach(series => {
            series.dataItems.forEach(di => {
              di.bullets.forEach(bullet => {
                setTimeout(() => bullet.get('sprite').set('opacity', 1), 3000);
              });
            });
          });
        }
        */
      });

      legend.data.setAll(sampleSeries);

      // Series factories
      function createSeriesPontuacao(dimension) {
        const field = dimension + '|' + SERIES_TYPES.pontuacao.field;
        const name = SERIES_TYPES.pontuacao.name;
        const series = makeCategorySeries(field, name, 90);

        series.columns.template.adapters.add('fill', (fill, target) => getFillRangeColor(fill, target));

        const tooltip = am5.Tooltip.new(root, {
          autoTextColor: false,
          getFillFromSprite: false,
          getLabelFillFromSprite: false,
        });
        tooltip.label.setAll({
          fill: am5.color('#FFFFFF'),
        });
        tooltip.get('background').setAll({
          fill: am5.color('#000000'),
          fillOpacity: 0.8,
        });
        series.set('tooltip', tooltip);
        tooltip.get('background').set('fill', am5.color('#000'));

        series.columns.template.adapters.add('tooltipText', (text, target) => {
          let newText = `[bold]${DIMENSIONS[dimension].name}[/]\n`;
          newText += 'Ano base: {categoryX}\n';
          newText += 'Pontuação: {valueY.formatNumber("0.0000")}[/]\n';
          newText += `Pontuação bruta: {${dimension}|${SERIES_TYPES.pontuacao_bruta.field}.formatNumber("0.0000")}\n`;
          newText += `Bonificação: {${dimension}|${SERIES_TYPES.bonificacao.field}.formatNumber("0.0000")}`;

          const data = target?.dataItem?.dataContext || {};

          if (data[dimension + '|vl_pontuacao_acreditacao'] != null) {
            newText += `\nAcreditação: {${dimension}|vl_pontuacao_acreditacao}`;
          }

          return newText;
        });

        // Bottom label
        series.bullets.push(root =>
          am5.Bullet.new(root, {
            locationY: 0,
            sprite: am5.Label.new(root, {
              text: DIMENSIONS[dimension].label || dimension,
              // oversizedBehavior: 'fit',
              centerX: am5.p50,
              centerY: am5.p0,
              fill: am5.color('#000'),
            }),
          }),
        );

        groupSeries('pontuacao', series);
        return series;
      }

      function createSeriesBruta(dimension) {
        const field = dimension + '|' + SERIES_TYPES.pontuacao_bruta.field;
        const name = SERIES_TYPES.pontuacao_bruta.name;
        const series = makeCategorySeries(field, name, 60, true);

        series.columns.template.adapters.add('fill', (fill, target) => getFillRangeColor(fill, target));
        series.columns.template.set('fillPattern', createLineBackground('#FFF'));

        groupSeries('pontuacao_bruta', series);
        return series;
      }

      function createSeriesBonificacao(dimension) {
        /**-/
        console.log('[Dim] createSeriesBonificacao', dimension)
        let series;
        series.name = SERIES_TYPES.bonificacao.name;
        series.yAxis = categoryBonificacaoAxis;
        groupSeries('bonificacao', series);
        return series;
        /**/
      }

      function createSeriesPrerequisito(dimension, index: number) {
        const field = dimension + '|' + SERIES_TYPES.prerequisito.field;
        const name = SERIES_TYPES.prerequisito.name;
        const color = SERIES_TYPES.prerequisito.color;

        const series = makeCategoryMarker(field, name, color, index);

        groupSeries('prerequisito', series);
        return series;
      }

      function createSeriesAcreditacao(dimension, index: number) {
        const field = dimension + '|' + SERIES_TYPES.acreditacao.field;
        const name = SERIES_TYPES.acreditacao.name;
        const color = SERIES_TYPES.acreditacao.color;

        const series = makeCategoryMarker(field, name, color, index);

        groupSeries('acreditacao', series);
        return series;
      }

      function groupSeries(type: string, series: am5xy.XYSeries) {
        const group = seriesMap[type];
        group.list.push(series);
      }

      function getLegendColor(fill: am5.Color, target: am5.Sprite | any) {
        const name: string = target?.dataItem?.get('name');

        switch (name) {
          case SERIES_TYPES.pontuacao.name:
            return am5.color('#000');

          case SERIES_TYPES.pontuacao_bruta.name:
            return am5.color('#AAA');

          case SERIES_TYPES.prerequisito.name:
            return am5.color(SERIES_TYPES.prerequisito.color);

          case SERIES_TYPES.acreditacao.name:
            return am5.color(SERIES_TYPES.acreditacao.color);

          default:
            return fill;
        }
      }

      function getFillRangeColor(fill: am5.Color, target: am5.Sprite | any) {
        const brighten = -0.2;
        let value = target?.dataItem?.get('valueY');

        if (value == null) return fill;

        if (value < 0.2) return am5.Color.brighten(am5.color('#CC0000'), brighten);

        if (value < 0.4) return am5.Color.brighten(am5.color('#FF6600'), brighten);

        if (value < 0.6) return am5.Color.brighten(am5.color('#FFC843'), brighten);

        if (value < 0.8) return am5.Color.brighten(am5.color('#D8FF00'), brighten);

        // value >= 0.8
        return am5.Color.brighten(am5.color('#78CC00'), brighten);
      }

      /**
       * Make chart series
       */
      function makeCategorySeries(field: string, name: string, size: number, centerY = false) {
        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name,
            maskBullets: false,
            xAxis: centerY ? categoryBrutaAxis : categoryAxis,
            yAxis: valueAxis,
            categoryXField: 'nr_ano_base',
            valueYField: field,
          }),
        );
        series.columns.template.setAll({
          opacity: 0.8,
          strokeWidth: 0,
          strokeOpacity: 0,
          width: am5.percent(size),
        });

        series.bullets.push(() =>
          am5.Bullet.new(root, {
            locationX: 0.5,
            locationY: centerY ? 0.5 : 1,
            sprite: am5.Label.new(root, {
              text: '[bold]{valueY.formatNumber("0.0000")}[/]',
              populateText: true,
              dy: -12,
              centerX: am5.percent(50),
              centerY: am5.percent(50),
              paddingTop: 1,
              paddingBottom: 1,
              paddingLeft: 0,
              paddingRight: 0,
              fill: am5.color('#000'),
              background: am5.Rectangle.new(root, {
                fill: am5.color('#fff'),
                fillOpacity: 0.5,
              }),
            }),
          }),
        );

        return series;
      }

      /**
       * Create chart series markers.
       */
      function makeCategoryMarker(field: string, name: string, color: string, index: number) {
        const series = chart.series.push(
          am5xy.StepLineSeries.new(root, {
            name,
            categoryXField: 'nr_ano_base',
            valueYField: field,
            xAxis: categoryBrutaAxis,
            yAxis: valueAxis,
            noRisers: true,
            stroke: am5.color(color),
          }),
        );
        series.strokes.template.setAll({
          strokeWidth: 2,
        });

        const min = CELL_START_LOCATION;
        const max = CELL_END_LOCATION;
        const size = (max - min) / Object.keys(DIMENSIONS).length;
        const padding = 0.08;

        series.setAll({
          locationX: padding + min + size * index,
          stepWidth: am5.percent((100 * (max - min)) / Object.keys(DIMENSIONS).length),
        });

        return series;
      }

      /**
       * Create a line background given a color
       */
      function createLineBackground(color: string): any {
        return am5.LinePattern.new(root, {
          color: am5.color(color),
          rotation: 45,
          width: 10,
          height: 10,
        });
      }

      // Instance refs
      this._root = root;
      this._chart = chart;
      this._title = title;
      this._export = exporting;
      this._yearAxis = categoryAxis;
      this._figuras = [logo, legendaFaixas];

      this._sinalizarPronto();
    });
  }

  ngOnDestroy(): void {
    // Clean up chart when the component is removed
    if (this._root) {
      this._root.dispose();
    }
  }

  private preprocessData(data) {
    this._yearAxis?.axisRanges.clear();

    data = data.reduce((acc, d) => {
      let yearGroup = acc.find(g => g['nr_ano_base'] == d['nr_ano_base']);
      if (!yearGroup) {
        yearGroup = {
          nr_ano_base: d['nr_ano_base'],
          ds_subtitulo: d['ds_subtitulo'],
        };
        acc.push(yearGroup);

        // Create Ranges for custom years names
        const rangeDataItem = this._yearAxis.makeDataItem({
          category: yearGroup['nr_ano_base'],
          endCategory: yearGroup['nr_ano_base'],
        });
        const range = this._yearAxis.createAxisRange(rangeDataItem);
        range.get('label').setAll({
          visible: true,
          text: yearGroup['ds_subtitulo'],
          fontSize: 16,
          fontWeight: 'bold',
          fill: am5.color('#808080'),
        });
        range.get('grid').set('visible', false);
      }

      // Series fields
      Object.keys(SERIES_TYPES).forEach(type => {
        let field = SERIES_TYPES[type].field;
        yearGroup[d['sg_dimensao'] + '|' + field] = d[field];
      });

      // Extra fields
      yearGroup[d['sg_dimensao'] + '|vl_pontuacao_acreditacao'] = d['vl_pontuacao_acreditacao'];

      return acc;
    }, []);

    // console.log('[Dim] processed', data);
    return data;
  }

  async exportarPng(larguraMinima: number): Promise<string> {
    await Promise.race([this._pronto, this.esperar(ESPERA_MAXIMA_MONTAGEM)]);

    if (!this._export) {
      throw new Error('O gráfico de dimensões não ficou pronto para exportação.');
    }

    await this.aguardarFiguras();

    await this.aguardarQuadro();
    await this.aguardarQuadro();

    const opcoes = { minWidth: larguraMinima } as OpcoesDeExport;

    return this._export.export('png', opcoes);
  }

  private esperar(milissegundos: number): Promise<void> {
    return new Promise<void>(resolve => setTimeout(resolve, milissegundos));
  }

  private aguardarQuadro(): Promise<void> {
    return Promise.race([
      new Promise<void>(resolve => this._root.events.once('frameended', () => resolve())),
      this.esperar(ESPERA_MAXIMA_QUADRO),
    ]);
  }

  private aguardarFiguras(): Promise<void[]> {
    const carregamentos = this._figuras
      .map(figura => figura?.get('src'))
      .filter(Boolean)
      .map(
        src =>
          new Promise<void>(resolve => {
            const imagem = new Image();
            imagem.onload = () => resolve();
            imagem.onerror = () => resolve();
            imagem.src = src;
          }),
      );

    return Promise.all(carregamentos);
  }

  @Input()
  set data(data: any) {
    if (!data || !this._root) {
      return;
    }

    const processedData = this.preprocessData(data);
    this._series.forEach(series => series.data.setAll(processedData));
    this._axis.forEach(axis => axis.data.setAll(processedData));
  }

  @Input()
  set title(title: string) {
    if (!this._title) {
      this.titleText = title;
      return;
    }

    this._title.set('text', TITLE_PREFIX + (title ? ' - (' + title + ')' : ''));
  }

  @Input()
  set filename(filename: string) {
    if (!this._root) {
      this.filenameValue = filename;
      return;
    }

    this._export?.set('filePrefix', filename);
  }
}
